/**
 * PackHeroStage — the browsable stage for a Toile pack page.
 *
 * Copied from `@product/narcis/packs/PackHeroStage.tsx` and cut to the one state Toile is
 * permanently in: narcis's `doorTier === 't3_professional'` + `visualStage === 'ad'` showcase — a
 * gallery of already-rendered cells with no veil, no BEFORE inlet, no try-on path (plan
 * 2026_08_30_toile_pack_page_convergence, decisions 1-2).
 *
 * The pager (prev/next zones as real buttons, the axis-tested swipe with its click suppression,
 * ←/→ read off the card) is the shared one in `@core/packs/stage/` (plan
 * 2026_09_23_pack_stage_coda.md). Movement is one callback, `onSelect(idx, method)`, owned by the
 * template, so zone, swipe, key, thumb and the aside's coda door land on the same state and the
 * same `pack_look_advanced` fire.
 *
 * The coda (plan § Decided 1–4): after the last look the stage has one more frame — the wall's
 * ghost at stage size over the nine faded — and it holds the page's action, `action`, which the
 * template creates once. The coda frame is ALWAYS mounted and only hidden while a look shows: the
 * action (`StageComponent`) must mount exactly once, because its sign-in return re-submits from a
 * mount effect and holds its `request_id` per instance — unmounting it on every page-away would
 * drop the visitor's words and a pending press. The swipe and key handlers sit on the frame, not in
 * the zones, so the coda pages too; the zones render only over a look (the coda spends its own
 * clicks).
 *
 * On a look, the look door (`✎ Change this one`) goes to the wall armed on that picture where the
 * wall knows every cell (`travel.cellsTravel`, the grid's own predicate); the corner tag moves left
 * while it shows.
 *
 * Toile's own twist (plan 2026_08_30 decision 3): the stage takes the PACK's ratio, per selected
 * cell — a coloring page is A4 and a wallpaper is wide. The coda takes the pack's default ratio as
 * its minimum shape.
 */

import type {ReactNode} from 'react'

import {track} from '@core/utils/analytics'
import {basisWallPath} from '@core/wall/pageVerb'
import {codaIndex, isCoda, nextLookIndex, withCoda} from '@core/packs/stage/pager'
import {usePagerGestures} from '@core/packs/stage/usePagerGestures'
import {tileIdAt} from '@core/packs/stage/travel'
import {StageCoda} from '@core/packs/stage/StageCoda'
import {CodaThumb} from '@core/packs/stage/CodaThumb'
import {LookDoor} from '@core/packs/stage/LookDoor'
import {TOILE_PACK_SAMPLES_BASE_URL} from '@product/toile/config'
import {packCells, packRatio, type ToilePack} from './data'
import {pagerLookIndices} from './pagerLooks'
import {CODA_LEAD, CODA_SUB, LOOK_DOOR_LABEL, codaThumbLabel} from './copy'
import styles from './PackHeroStage.module.scss'

export type LookMethod = 'tap' | 'swipe' | 'arrow' | 'thumb' | 'door'

interface PackHeroStageProps {
    pack: ToilePack
    /** A look index, or the coda's (`codaIndex(cells)`). */
    selected: number
    onSelect: (index: number, method: LookMethod) => void
    /** The page's action, placed in the coda. Created by the template, once. */
    action: ReactNode
    /** The nine cells as the wall knows them, by position — the look door's destination. */
    tileIdsByPosition?: readonly (number | null)[]
}

export function PackHeroStage({pack, selected, onSelect, action, tileIdsByPosition = []}: PackHeroStageProps) {
    const cells = packCells(pack)
    const coda = codaIndex(cells.length)
    const onCoda = isCoda(selected, cells.length)
    const cell = onCoda ? null : cells[selected]
    const stripRatio = packRatio(pack)
    const cellRatio = cell ? packRatio(pack, pack.styles[selected % 3].key) : stripRatio
    const base = `${TOILE_PACK_SAMPLES_BASE_URL}/${pack.slug}`

    const pagerIndices = withCoda(pagerLookIndices(cells.length), coda)

    function advanceLook(delta: 1 | -1, method: 'tap' | 'swipe' | 'arrow') {
        const next = nextLookIndex(pagerIndices, selected, delta)
        if (next === null) return
        onSelect(next, method)
    }
    const pagerGestures = usePagerGestures(advanceLook)

    const doorTileId = cell ? tileIdAt(tileIdsByPosition, selected) : null

    return (
        <section
            className={styles.stage}
            aria-label="Sample images from this pack"
            id="stage"
            // The frame is the pack's, per cell (--tl-stage-ratio drives aspect-ratio; --tl-stage-ar
            // is the same number, unitless, so the height budget below can derive a width from it).
            style={{
                '--tl-stage-ratio': `${cellRatio.w} / ${cellRatio.h}`,
                '--tl-stage-ar': `${cellRatio.w / cellRatio.h}`,
                '--tl-thumb-ratio': `${stripRatio.w} / ${stripRatio.h}`,
            } as React.CSSProperties}
        >
            {/* The frame takes the gestures, so a swipe or a key pages from a look and from the coda
                alike; the gesture layer ignores keys and touches that start in the coda's field. */}
            <div
                className={styles.frame}
                onTouchStart={pagerGestures.onTouchStart}
                onTouchEnd={pagerGestures.onTouchEnd}
                onKeyDown={pagerGestures.onKeyDown}
            >
                {cell && (
                    <div className={styles.bigArea}>
                        <img
                            src={`${base}/${cell.image}`}
                            alt={`${pack.name} — ${cell.subject}, ${cell.style}`}
                            className={styles.bigImage}
                            loading="eager"
                        />

                        {/* Card as pager. The zones cover the whole card and sit under the look door
                            and the corner tag (which is pointer-events:none). */}
                        {pagerIndices.length > 1 && (
                            <div className={styles.pager}>
                                <button
                                    type="button"
                                    className={`${styles.pagerZone} ${styles.pagerZonePrev}`}
                                    onClick={() => pagerGestures.onZoneClick(-1)}
                                    aria-label="previous sample"
                                >
                                    <span className={styles.pagerArrow} aria-hidden="true">←</span>
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.pagerZone} ${styles.pagerZoneNext}`}
                                    onClick={() => pagerGestures.onZoneClick(1)}
                                    aria-label="next sample"
                                >
                                    <span className={styles.pagerArrow} aria-hidden="true">→</span>
                                </button>
                            </div>
                        )}

                        <div
                            className={`${styles.cornerTag} ${doorTileId !== null ? styles.cornerTagLeft : ''}`}
                            aria-hidden="true"
                        >
                            {pack.readings[Math.floor(selected / 3)].label.toUpperCase()} · {pack.styles[selected % 3].label.toUpperCase()}
                        </div>

                        {doorTileId !== null && (
                            <LookDoor
                                href={basisWallPath(doorTileId)}
                                label={LOOK_DOOR_LABEL}
                                onFollow={() => track('pack_look_change', {pack_slug: pack.slug, look_index: selected})}
                            />
                        )}
                    </div>
                )}

                <div className={styles.codaSlot} hidden={!onCoda}>
                    <StageCoda
                        backdrop={`${base}/${cells[cells.length - 1].image}`}
                        ratio={stripRatio}
                        lead={CODA_LEAD}
                        head={pack.name}
                        sub={CODA_SUB}
                    >
                        {action}
                    </StageCoda>
                </div>
            </div>

            <div className={styles.thumbStrip} role="list" aria-label={`${cells.length} samples. Tap to see each`}>
                {cells.map((c, i) => {
                    const isActive = i === selected
                    const r = pack.readings[Math.floor(i / 3)]
                    const s = pack.styles[i % 3]
                    return (
                        <button
                            key={c.image}
                            type="button"
                            className={`${styles.thumb} ${isActive ? styles.thumbActive : ''}`}
                            onClick={() => onSelect(i, 'thumb')}
                            aria-label={`${r.label}, ${s.label}`}
                            aria-pressed={isActive}
                            role="listitem"
                        >
                            <img
                                src={`${base}/${c.image}`}
                                alt=""
                                className={styles.thumbImage}
                                loading="lazy"
                                decoding="async"
                                draggable={false}
                            />
                        </button>
                    )
                })}
                <CodaThumb
                    className={styles.thumbSlot}
                    on={onCoda}
                    label={codaThumbLabel(pack.name)}
                    onSelect={() => onSelect(coda, 'thumb')}
                    role="listitem"
                />
            </div>
        </section>
    )
}
